import React, { useEffect, useState } from "react";
import { Alert, Badge, Button, Col, Container, Form, Row, Spinner, Table } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faClose, faPlus, faSave, faTimes, faTrash } from "@fortawesome/free-solid-svg-icons";
import * as Constants from "../../../constants";
import classes from "./CustomerForm.module.css";

import {
    ESTADOS_CIVIS,
    GRAUS_PARENTESCO,
    SEXOS,
    TIPOS_PESSOA,
    UFS,
    cnpjValido,
    cpfValido,
    formatarCep,
    formatarCpf,
    formatarDocumento,
    formatarTelefone,
    hojeISO,
    normalizarDocumento,
    rotulosPorTipo,
    somaPercentuais,
    somenteDigitos,
} from "./customersUtils";

const recipientVazio = {
    nome: "",
    cpf: "",
    data_nascimento: "",
    grau_parentesco: "",
    percentual_participacao: "",
};

function CustomerForm({ customer, onSave, onCancel }) {
    const editando = !!customer?.id;

    // Estados do Formulário Principal
    const [tipoPessoa, setTipoPessoa] = useState("F");
    const [nomeRazaoSocial, setNomeRazaoSocial] = useState("");
    const [cpfCnpj, setCpfCnpj] = useState("");
    const [rgInscricaoEstadual, setRgInscricaoEstadual] = useState("");
    const [dataNascimentoFundacao, setDataNascimentoFundacao] = useState("");
    const [sexo, setSexo] = useState("");
    const [estadoCivil, setEstadoCivil] = useState("");
    const [profissaoRamoAtividade, setProfissaoRamoAtividade] = useState("");
    const [email, setEmail] = useState("");
    const [telefonePrincipal, setTelefonePrincipal] = useState("");
    const [telefoneSecundario, setTelefoneSecundario] = useState("");
    const [cep, setCep] = useState("");
    const [logradouro, setLogradouro] = useState("");
    const [numero, setNumero] = useState("");
    const [complemento, setComplemento] = useState("");
    const [bairro, setBairro] = useState("");
    const [cidade, setCidade] = useState("");
    const [estado, setEstado] = useState("");
    const [observacoes, setObservacoes] = useState("");
    const [ativo, setAtivo] = useState(true);

    // Lista de Beneficiários e Auxiliares
    const [recipients, setRecipients] = useState([]);
    const [carregandoRecipients, setCarregandoRecipients] = useState(false);
    const [erroCargaRecipients, setErroCargaRecipients] = useState(null);
    const [erroSalvar, setErroSalvar] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [validated, setValidated] = useState(false);

    // Carregar ou Reinicializar Dados do Customer
    useEffect(() => {
        if (customer) {
            setTipoPessoa(customer.tipo_pessoa || "F");
            setNomeRazaoSocial(customer.nome_razao_social || "");
            setCpfCnpj(customer.cpf_cnpj || "");
            setRgInscricaoEstadual(customer.rg_inscricao_estadual || "");
            setDataNascimentoFundacao((customer.data_nascimento_fundacao || "").slice(0, 10));
            setSexo(customer.sexo || "");
            setEstadoCivil(customer.estado_civil || "");
            setProfissaoRamoAtividade(customer.profissao_ramo_atividade || "");
            setEmail(customer.email || "");
            setTelefonePrincipal(customer.telefone_principal || "");
            setTelefoneSecundario(customer.telefone_secundario || "");
            setCep(customer.cep || "");
            setLogradouro(customer.logradouro || "");
            setNumero(customer.numero || "");
            setComplemento(customer.complemento || "");
            setBairro(customer.bairro || "");
            setCidade(customer.cidade || "");
            setEstado(customer.estado || "");
            setObservacoes(customer.observacoes || "");
            setAtivo(customer.ativo ?? true);
        }

        // Buscar Beneficiários se for Edição
        if (customer?.id) {
            const controller = new AbortController();
            const fetchData = async () => {
                try {
                    setCarregandoRecipients(true);
                    setErroCargaRecipients(null);
                    const token = localStorage.getItem("token");

                    const res = await fetch(`${Constants.RECIPIENTS_LIST}/?customer_id=${customer.id}`, {
                        signal: controller.signal,
                        headers: {
                            Authorization: `Bearer ${token}`,
                            "Content-Type": "application/json",
                        },
                    });
                    const json = await res.json();

                    if (json.status === "success") {
                        setRecipients(
                            (json.data || []).map((b) => ({
                                id: b.id,
                                nome: b.nome || "",
                                cpf: b.cpf || "",
                                data_nascimento: (b.data_nascimento || "").slice(0, 10),
                                grau_parentesco: b.grau_parentesco || "",
                                percentual_participacao: b.percentual_participacao ?? "",
                            }))
                        );
                    } else {
                        setErroCargaRecipients(json.message || "Erro ao carregar beneficiários.");
                    }
                } catch (error) {
                    if (error.name !== "AbortError") {
                        console.error("Erro ao carregar beneficiários:", error);
                        setErroCargaRecipients(error.message);
                    }
                } finally {
                    setCarregandoRecipients(false);
                }
            };

            fetchData();
            return () => controller.abort();
        } else {
            setRecipients([]);
        }
    }, [customer]);

    // Troca de Tipo de Pessoa (PF x PJ)
    const handleTipoPessoaChange = (e) => {
        const novoTipo = e.target.value;
        setTipoPessoa(novoTipo);
        setCpfCnpj("");
        if (novoTipo === "J") {
            setSexo("");
            setEstadoCivil("");
        }
    };

    // Auxiliares de Beneficiários
    const handleRecipientChange = (index, field, value) => {
        setRecipients((prev) => {
            const lista = [...prev];
            lista[index] = { ...lista[index], [field]: value };
            return lista;
        });
    };

    const handleAddRecipient = () => {
        setRecipients((prev) => [...prev, { ...recipientVazio }]);
    };

    const handleRemoveRecipient = (index) => {
        setRecipients((prev) => prev.filter((_, i) => i !== index));
    };

    // Validações
    const pj = tipoPessoa === "J";
    const rotulos = rotulosPorTipo(tipoPessoa);

    const isNomeValid = nomeRazaoSocial.trim().length > 0 && nomeRazaoSocial.length <= 200;
    const isDocumentoValid = pj ? cnpjValido(cpfCnpj) : cpfValido(cpfCnpj);
    const isDataNascimentoValid = !dataNascimentoFundacao || dataNascimentoFundacao <= hojeISO();
    const isEmailValid = !email || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    const isTelPrincipalValid = !telefonePrincipal || [10, 11].includes(somenteDigitos(telefonePrincipal).length);
    const isTelSecundarioValid = !telefoneSecundario || [10, 11].includes(somenteDigitos(telefoneSecundario).length);
    const isCepValid = !cep || somenteDigitos(cep).length === 8;

    const somaPerc = somaPercentuais(recipients);
    const isRecipientsValid = recipients.every((b) => {
        const nomeOk = b.nome.trim().length > 0;
        const cpfOk = !b.cpf || cpfValido(b.cpf);
        const dataOk = !b.data_nascimento || b.data_nascimento <= hojeISO();
        const parentescoOk = !!b.grau_parentesco;
        const perc = Number(b.percentual_participacao);
        const percOk = !isNaN(perc) && perc > 0 && perc <= 100;
        return nomeOk && cpfOk && dataOk && parentescoOk && percOk;
    }) && somaPerc <= 100;

    const isFormValid =
        isNomeValid &&
        isDocumentoValid &&
        isDataNascimentoValid &&
        isEmailValid &&
        isTelPrincipalValid &&
        isTelSecundarioValid &&
        isCepValid &&
        isRecipientsValid;

    const handleSubmit = async (event) => {
        event.preventDefault();
        setValidated(true);
        setErroSalvar(null);

        const form = event.currentTarget;
        if (form.checkValidity() === false || !isFormValid) {
            event.stopPropagation();
            return;
        }

        const payload = {
            ...(customer?.id ? { id: customer.id } : {}),
            tipo_pessoa: tipoPessoa,
            nome_razao_social: nomeRazaoSocial.trim(),
            cpf_cnpj: somenteDigitos(cpfCnpj),
            rg_inscricao_estadual: rgInscricaoEstadual,
            data_nascimento_fundacao: dataNascimentoFundacao || null,
            sexo: pj ? "" : sexo,
            estado_civil: pj ? "" : estadoCivil,
            profissao_ramo_atividade: profissaoRamoAtividade,
            email: email.trim(),
            telefone_principal: somenteDigitos(telefonePrincipal),
            telefone_secundario: somenteDigitos(telefoneSecundario),
            cep: somenteDigitos(cep),
            logradouro,
            numero,
            complemento,
            bairro,
            cidade,
            estado,
            observacoes,
            ativo,
            recipients: recipients.map((r) => ({
                ...(r.id ? { id: r.id } : {}),
                nome: r.nome.trim(),
                cpf: somenteDigitos(r.cpf),
                data_nascimento: r.data_nascimento || null,
                grau_parentesco: r.grau_parentesco,
                percentual_participacao: parseFloat(r.percentual_participacao),
            })),
        };

        try {
            setIsSubmitting(true);
            if (onSave) {
                await onSave(payload);
            }
        } catch (error) {
            setErroSalvar(error.message || "Erro ao salvar o registro.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const corSoma = somaPerc > 100 ? "danger" : somaPerc === 100 ? "success" : "warning";
    const bloqueado = editando && (carregandoRecipients || !!erroCargaRecipients);

    return (
        <Container fluid className="mt-4 mb-5">
            <Form noValidate validated={validated} onSubmit={handleSubmit} className={classes.customerForm}>
                <h5 className="text-primary border-bottom pb-2 mb-3">Dados Principais</h5>

                <Row className="mb-3">
                    <Col md={3}>
                        <Form.Group controlId="formTipoPessoa">
                            <Form.Label>Tipo de pessoa:</Form.Label>
                            <Form.Select value={tipoPessoa} onChange={handleTipoPessoaChange} required>
                                {TIPOS_PESSOA.map((t) => (
                                    <option key={t.value} value={t.value}>
                                        {t.label}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    </Col>
                    <Col md={9}>
                        <Form.Group controlId="formNomeRazaoSocial">
                            <Form.Label>{rotulos.nome}:</Form.Label>
                            <Form.Control
                                type="text"
                                value={nomeRazaoSocial}
                                required
                                isValid={validated && isNomeValid}
                                isInvalid={validated && !isNomeValid}
                                onChange={(e) => setNomeRazaoSocial(e.target.value)}
                            />
                            <Form.Control.Feedback type="invalid">
                                <small>
                                    <FontAwesomeIcon icon={faTimes} /> Informe o nome ou razão social (máx. 200).
                                </small>
                            </Form.Control.Feedback>
                            <Form.Control.Feedback type="valid">
                                <small>
                                    <FontAwesomeIcon icon={faCheck} /> Nome válido.
                                </small>
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                </Row>

                <Row className="mb-3">
                    <Col md={4}>
                        <Form.Group controlId="formCpfCnpj">
                            <Form.Label>{rotulos.documento}:</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder={pj ? "00.000.000/0000-00" : "000.000.000-00"}
                                value={formatarDocumento(cpfCnpj, tipoPessoa)}
                                required
                                isValid={validated && isDocumentoValid}
                                isInvalid={validated && !isDocumentoValid}
                                onChange={(e) => setCpfCnpj(normalizarDocumento(e.target.value, tipoPessoa))}
                            />
                            <Form.Control.Feedback type="invalid">
                                <small>
                                    <FontAwesomeIcon icon={faTimes} /> {pj ? "CNPJ inválido." : "CPF inválido."}
                                </small>
                            </Form.Control.Feedback>
                            <Form.Control.Feedback type="valid">
                                <small>
                                    <FontAwesomeIcon icon={faCheck} /> Documento válido.
                                </small>
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                    <Col md={4}>
                        <Form.Group controlId="formRgInscricao">
                            <Form.Label>{rotulos.registro}:</Form.Label>
                            <Form.Control
                                type="text"
                                value={rgInscricaoEstadual}
                                onChange={(e) => setRgInscricaoEstadual(e.target.value)}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={4}>
                        <Form.Group controlId="formDataNascimento">
                            <Form.Label>{rotulos.data}:</Form.Label>
                            <Form.Control
                                type="date"
                                value={dataNascimentoFundacao}
                                isValid={validated && isDataNascimentoValid && !!dataNascimentoFundacao}
                                isInvalid={validated && !isDataNascimentoValid}
                                onChange={(e) => setDataNascimentoFundacao(e.target.value)}
                            />
                            <Form.Control.Feedback type="invalid">
                                <small>
                                    <FontAwesomeIcon icon={faTimes} /> A data não pode estar no futuro.
                                </small>
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                </Row>

                {!pj && (
                    <Row className="mb-3">
                        <Col md={4}>
                            <Form.Group controlId="formSexo">
                                <Form.Label>Sexo:</Form.Label>
                                <Form.Select value={sexo} onChange={(e) => setSexo(e.target.value)}>
                                    <option value="">Selecione</option>
                                    {SEXOS.map((s) => (
                                        <option key={s.value} value={s.value}>
                                            {s.label}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group controlId="formEstadoCivil">
                                <Form.Label>Estado civil:</Form.Label>
                                <Form.Select value={estadoCivil} onChange={(e) => setEstadoCivil(e.target.value)}>
                                    <option value="">Selecione</option>
                                    {ESTADOS_CIVIS.map((c) => (
                                        <option key={c.value} value={c.value}>
                                            {c.label}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group controlId="formProfissao">
                                <Form.Label>{rotulos.atividade}:</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={profissaoRamoAtividade}
                                    onChange={(e) => setProfissaoRamoAtividade(e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                )}

                {pj && (
                    <Row className="mb-3">
                        <Col md={12}>
                            <Form.Group controlId="formRamoAtividade">
                                <Form.Label>{rotulos.atividade}:</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={profissaoRamoAtividade}
                                    onChange={(e) => setProfissaoRamoAtividade(e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                )}

                <h5 className="text-primary border-bottom pb-2 mb-3 mt-4">Contato</h5>
                <Row className="mb-3">
                    <Col md={6}>
                        <Form.Group controlId="formEmail">
                            <Form.Label>E-mail:</Form.Label>
                            <Form.Control
                                type="email"
                                value={email}
                                isValid={validated && isEmailValid && !!email}
                                isInvalid={validated && !isEmailValid}
                                onChange={(e) => setEmail(e.target.value)}
                            />
                            <Form.Control.Feedback type="invalid">
                                <small>
                                    <FontAwesomeIcon icon={faTimes} /> Forneça um e-mail válido.
                                </small>
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                    <Col md={3}>
                        <Form.Group controlId="formTelPrincipal">
                            <Form.Label>Telefone Principal:</Form.Label>
                            <Form.Control
                                type="text"
                                value={formatarTelefone(telefonePrincipal)}
                                isValid={validated && isTelPrincipalValid && !!telefonePrincipal}
                                isInvalid={validated && !isTelPrincipalValid}
                                onChange={(e) => setTelefonePrincipal(somenteDigitos(e.target.value).slice(0, 11))}
                            />
                            <Form.Control.Feedback type="invalid">
                                <small>
                                    <FontAwesomeIcon icon={faTimes} /> Informe DDD + Número (10 ou 11 dígitos).
                                </small>
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                    <Col md={3}>
                        <Form.Group controlId="formTelSecundario">
                            <Form.Label>Telefone Secundário:</Form.Label>
                            <Form.Control
                                type="text"
                                value={formatarTelefone(telefoneSecundario)}
                                isValid={validated && isTelSecundarioValid && !!telefoneSecundario}
                                isInvalid={validated && !isTelSecundarioValid}
                                onChange={(e) => setTelefoneSecundario(somenteDigitos(e.target.value).slice(0, 11))}
                            />
                            <Form.Control.Feedback type="invalid">
                                <small>
                                    <FontAwesomeIcon icon={faTimes} /> Informe DDD + Número (10 ou 11 dígitos).
                                </small>
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                </Row>

                <h5 className="text-primary border-bottom pb-2 mb-3 mt-4">Endereço</h5>
                <Row className="mb-3">
                    <Col md={3}>
                        <Form.Group controlId="formCep">
                            <Form.Label>CEP:</Form.Label>
                            <Form.Control
                                type="text"
                                value={formatarCep(cep)}
                                isValid={validated && isCepValid && !!cep}
                                isInvalid={validated && !isCepValid}
                                onChange={(e) => setCep(somenteDigitos(e.target.value).slice(0, 8))}
                            />
                            <Form.Control.Feedback type="invalid">
                                <small>
                                    <FontAwesomeIcon icon={faTimes} /> O CEP deve conter 8 dígitos.
                                </small>
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group controlId="formLogradouro">
                            <Form.Label>Logradouro:</Form.Label>
                            <Form.Control
                                type="text"
                                value={logradouro}
                                onChange={(e) => setLogradouro(e.target.value)}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={3}>
                        <Form.Group controlId="formNumero">
                            <Form.Label>Número:</Form.Label>
                            <Form.Control
                                type="text"
                                value={numero}
                                onChange={(e) => setNumero(e.target.value)}
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <Row className="mb-3">
                    <Col md={3}>
                        <Form.Group controlId="formComplemento">
                            <Form.Label>Complemento:</Form.Label>
                            <Form.Control
                                type="text"
                                value={complemento}
                                onChange={(e) => setComplemento(e.target.value)}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={3}>
                        <Form.Group controlId="formBairro">
                            <Form.Label>Bairro:</Form.Label>
                            <Form.Control
                                type="text"
                                value={bairro}
                                onChange={(e) => setBairro(e.target.value)}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={4}>
                        <Form.Group controlId="formCidade">
                            <Form.Label>Cidade:</Form.Label>
                            <Form.Control
                                type="text"
                                value={cidade}
                                onChange={(e) => setCidade(e.target.value)}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={2}>
                        <Form.Group controlId="formEstado">
                            <Form.Label>UF:</Form.Label>
                            <Form.Select value={estado} onChange={(e) => setEstado(e.target.value)}>
                                <option value="">Selecione</option>
                                {UFS.map((u) => (
                                    <option key={u.value} value={u.value}>
                                        {u.value}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    </Col>
                </Row>

                <Row className="mb-3">
                    <Col md={12}>
                        <Form.Group controlId="formObservacoes">
                            <Form.Label>Observações:</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={observacoes}
                                onChange={(e) => setObservacoes(e.target.value)}
                            />
                        </Form.Group>
                    </Col>
                </Row>

                {editando && (
                    <Row className="mb-3">
                        <Col md={12}>
                            <Form.Check
                                type="switch"
                                id="customer-ativo-switch"
                                label="Cliente ativo"
                                checked={ativo}
                                onChange={(e) => setAtivo(e.target.checked)}
                            />
                        </Col>
                    </Row>
                )}

                {/* Seção Beneficiários */}
                <h5 className="text-primary border-bottom pb-2 mb-3 mt-4">Beneficiários</h5>

                {carregandoRecipients && (
                    <div className="py-3">
                        <Spinner animation="border" size="sm" /> Carregando beneficiários...
                    </div>
                )}

                {erroCargaRecipients && (
                    <Alert variant="danger" className="mb-3">
                        Não foi possível carregar os beneficiários deste cliente.
                        <div className="small mt-1">{erroCargaRecipients}</div>
                    </Alert>
                )}

                {!carregandoRecipients && !erroCargaRecipients && (
                    <>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <div>
                                <strong>Distribuído:</strong>{" "}
                                <Badge bg={corSoma} text={corSoma === "warning" ? "dark" : undefined}>
                                    {somaPerc.toLocaleString("pt-BR")}%
                                </Badge>
                                {somaPerc < 100 && recipients.length > 0 && (
                                    <span className="text-muted small ms-2">
                                        restam {(100 - somaPerc).toLocaleString("pt-BR")}%
                                    </span>
                                )}
                            </div>

                            <Button size="sm" variant="outline-primary" onClick={handleAddRecipient}>
                                <FontAwesomeIcon icon={faPlus} /> Adicionar beneficiário
                            </Button>
                        </div>

                        {somaPerc > 100 && (
                            <Alert variant="danger" className="py-2">
                                A soma dos percentuais dos beneficiários não pode ultrapassar 100%.
                            </Alert>
                        )}

                        {recipients.length === 0 ? (
                            <Alert variant="light" className="border text-center mb-3">
                                Nenhum beneficiário cadastrado para este cliente.
                            </Alert>
                        ) : (
                            <div className="table-responsive mb-3">
                                <Table bordered size="sm" className="align-middle mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th style={{ minWidth: 200 }}>Nome *</th>
                                            <th style={{ minWidth: 140 }}>CPF</th>
                                            <th style={{ minWidth: 140 }}>Nascimento</th>
                                            <th style={{ minWidth: 150 }}>Parentesco *</th>
                                            <th style={{ width: 120 }}>Percentual (%) *</th>
                                            <th style={{ width: 50 }}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {recipients.map((b, i) => {
                                            const cpfOk = !b.cpf || cpfValido(b.cpf);
                                            const nomeOk = b.nome.trim().length > 0;
                                            const parentescoOk = !!b.grau_parentesco;
                                            const percNum = Number(b.percentual_participacao);
                                            const percOk =
                                                b.percentual_participacao !== "" &&
                                                !isNaN(percNum) &&
                                                percNum > 0 &&
                                                percNum <= 100;

                                            return (
                                                <tr key={b.id ?? `novo-${i}`}>
                                                    <td>
                                                        <Form.Control
                                                            size="sm"
                                                            type="text"
                                                            value={b.nome}
                                                            required
                                                            isInvalid={validated && !nomeOk}
                                                            onChange={(e) =>
                                                                handleRecipientChange(i, "nome", e.target.value)
                                                            }
                                                        />
                                                    </td>
                                                    <td>
                                                        <Form.Control
                                                            size="sm"
                                                            type="text"
                                                            placeholder="000.000.000-00"
                                                            value={formatarCpf(b.cpf)}
                                                            isInvalid={validated && !cpfOk}
                                                            onChange={(e) =>
                                                                handleRecipientChange(
                                                                    i,
                                                                    "cpf",
                                                                    somenteDigitos(e.target.value).slice(0, 11)
                                                                )
                                                            }
                                                        />
                                                    </td>
                                                    <td>
                                                        <Form.Control
                                                            size="sm"
                                                            type="date"
                                                            value={b.data_nascimento}
                                                            onChange={(e) =>
                                                                handleRecipientChange(i, "data_nascimento", e.target.value)
                                                            }
                                                        />
                                                    </td>
                                                    <td>
                                                        <Form.Select
                                                            size="sm"
                                                            value={b.grau_parentesco}
                                                            required
                                                            isInvalid={validated && !parentescoOk}
                                                            onChange={(e) =>
                                                                handleRecipientChange(i, "grau_parentesco", e.target.value)
                                                            }
                                                        >
                                                            <option value="">Selecione</option>
                                                            {GRAUS_PARENTESCO.map((g) => (
                                                                <option key={g.value} value={g.value}>
                                                                    {g.label}
                                                                </option>
                                                            ))}
                                                        </Form.Select>
                                                    </td>
                                                    <td>
                                                        <Form.Control
                                                            size="sm"
                                                            type="number"
                                                            min="0"
                                                            max="100"
                                                            step="0.01"
                                                            value={b.percentual_participacao}
                                                            required
                                                            isInvalid={validated && !percOk}
                                                            onChange={(e) =>
                                                                handleRecipientChange(
                                                                    i,
                                                                    "percentual_participacao",
                                                                    e.target.value
                                                                )
                                                            }
                                                        />
                                                    </td>
                                                    <td className="text-center">
                                                        <Button
                                                            size="sm"
                                                            variant="outline-danger"
                                                            title="Remover beneficiário"
                                                            onClick={() => handleRemoveRecipient(i)}
                                                        >
                                                            <FontAwesomeIcon icon={faTrash} />
                                                        </Button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            </div>
                        )}
                    </>
                )}

                {erroSalvar && (
                    <Alert variant="danger" className="mt-3">
                        {erroSalvar}
                    </Alert>
                )}

                <div className="d-flex gap-2 mt-4">
                    <Button variant="primary" type="submit" disabled={bloqueado || isSubmitting}>
                        {isSubmitting ? (
                            <Spinner animation="border" size="sm" />
                        ) : (
                            <FontAwesomeIcon icon={faSave} />
                        )}{" "}
                        Salvar
                    </Button>

                    {onCancel && (
                        <Button variant="outline-secondary" type="button" onClick={onCancel}>
                            <FontAwesomeIcon icon={faClose} /> Cancelar
                        </Button>
                    )}
                </div>
            </Form>
        </Container >
    );
}

export default CustomerForm;